import React, { useCallback, useEffect, useState } from "react";
import { FiTrash2, FiChevronDown } from "react-icons/fi";
import { apiRequest, errorMessage } from "../../api";
import { budgetText, timeAgo, STATUS_LABELS, STATUS_STYLES } from "../../components/cowork/jobUtils";

const selectClass = "px-2 py-1 rounded-md bg-navy-dark border border-navy-border text-white text-xs focus:outline-none focus:border-accent-light";

function Proposals({ jobId, currency }) {
  const [list, setList] = useState(null);
  useEffect(() => {
    apiRequest(`/core/cowork/jobs/${jobId}/proposals/`, { auth: true }).then(({ ok, data }) => setList(ok ? data : []));
  }, [jobId]);
  if (!list) return <p className="text-xs text-gray-500 py-2">Loading...</p>;
  if (list.length === 0) return <p className="text-xs text-gray-500 py-2">No proposals.</p>;
  return (
    <div className="space-y-2 py-2">
      {list.map((p) => (
        <div key={p.id} className="rounded-md bg-white/5 border border-navy-border p-3 text-xs">
          <p>
            <span className="font-semibold text-white">{p.seller_name}</span>
            {p.seller_contact && <span className="text-gray-400"> · {p.seller_contact.email}</span>}
            <span className={`ml-2 px-2 py-0.5 rounded-full ${STATUS_STYLES[p.status]}`}>{STATUS_LABELS[p.status]}</span>
            <span className="float-right text-accent-light font-semibold">{currency} {p.bid_amount} · {p.delivery_days}d</span>
          </p>
          <p className="text-gray-300 mt-1 whitespace-pre-line">{p.cover_letter}</p>
        </div>
      ))}
    </div>
  );
}

export default function CoWorkJobsAdmin() {
  const [jobs, setJobs] = useState(null);
  const [status, setStatus] = useState("");
  const [open, setOpen] = useState(null);
  const [currency, setCurrency] = useState("USD");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    const { ok, data } = await apiRequest(`/core/cowork/jobs/${status ? `?status=${status}` : ""}`, { auth: true });
    setJobs(ok ? data : []);
  }, [status]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    apiRequest("/core/cowork/settings/").then(({ ok, data }) => ok && setCurrency(data.currency_label));
  }, []);

  const patch = async (job, changes) => {
    setError("");
    const { ok, data } = await apiRequest(`/core/cowork/jobs/${job.id}/`, { method: "PATCH", auth: true, body: changes });
    if (!ok) return setError(errorMessage(data));
    load();
  };

  const remove = async (job) => {
    if (!window.confirm(`Delete "${job.title}" and all its proposals?`)) return;
    await apiRequest(`/core/cowork/jobs/${job.id}/`, { method: "DELETE", auth: true });
    load();
  };

  return (
    <div className="w-full">
      <h1 className="text-2xl font-bold mb-2">Jobs</h1>
      <p className="text-sm text-gray-400 mb-6">
        Every job posted by buyers. Hide a job to remove it from the public jobs board without deleting it, or change its status.
      </p>

      <div className="flex flex-wrap gap-2 mb-4">
        {[["", "All"], ["open", "Open"], ["in_progress", "In progress"], ["completed", "Completed"], ["closed", "Closed"]].map(([key, label]) => (
          <button key={key} onClick={() => setStatus(key)} className={`px-4 py-1.5 rounded-full text-sm ${status === key ? "bg-brand-gradient text-white" : "bg-white/5 border border-navy-border text-gray-300"}`}>
            {label}
          </button>
        ))}
      </div>

      {error && <p className="text-red-400 mb-3">{error}</p>}

      <div className="bg-navy border border-navy-border rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-gray-400 text-left">
            <tr>
              <th className="px-4 py-3">Job</th>
              <th className="px-4 py-3">Buyer</th>
              <th className="px-4 py-3">Budget</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 w-20">Visible</th>
              <th className="px-4 py-3 w-32"></th>
            </tr>
          </thead>
          <tbody>
            {(jobs || []).map((job) => (
              <React.Fragment key={job.id}>
                <tr className="border-t border-navy-border align-top">
                  <td className="px-4 py-3">
                    <p className="text-white font-medium">{job.title}</p>
                    <p className="text-xs text-gray-500">{job.category || "—"} · {timeAgo(job.created_at)}</p>
                  </td>
                  <td className="px-4 py-3 text-gray-300 text-xs">{job.buyer_name}<br />{job.buyer_email}</td>
                  <td className="px-4 py-3 text-gray-300 text-xs">{budgetText(job, currency)}</td>
                  <td className="px-4 py-3">
                    <select className={selectClass} value={job.status} onChange={(e) => patch(job, { status: e.target.value })}>
                      {Object.entries(STATUS_LABELS).filter(([k]) => ["open", "in_progress", "completed", "closed"].includes(k)).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <input type="checkbox" checked={job.is_active} onChange={(e) => patch(job, { is_active: e.target.checked })} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button onClick={() => setOpen(open === job.id ? null : job.id)} className="flex items-center gap-1 text-xs text-accent-light">
                        Proposals ({job.proposals_count}) <FiChevronDown className={open === job.id ? "rotate-180" : ""} />
                      </button>
                      <button onClick={() => remove(job)} className="p-1.5 rounded-md text-red-400 hover:bg-red-500/10" aria-label="Delete"><FiTrash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
                {open === job.id && (
                  <tr className="border-t border-navy-border bg-navy-dark/40">
                    <td colSpan={6} className="px-4"><Proposals jobId={job.id} currency={currency} /></td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {jobs && jobs.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-500">No jobs yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
